import {
  useState,
  useEffect,
  memo,
  useRef,
  useCallback,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
} from 'react';
import SearchInput, { SearchInputRef } from '@/components/Search/components/SearchInput';
import styles from './index.module.less';
import { useSearchRecommends, suggestionBizMap } from './hooks';
import SearchRecommends, { EnumRowFlexDirection } from '@/components/Search/components/Recommends';
import Suggestions from '@/components/Search/components/Suggestions';
import { redirectToSearchResultsPage } from '@/pages/search/utils';
import HeightTransition from '@/components/HeightTransition';
import cls from 'classnames';
import Curtain from '../Curtain';
import { useTranslation } from '@/hooks/useTranslation';
import { SearchTracker } from '@/utils/Tracker';
import type { ISearchBarProps, SearchRef } from './typings';
import { removeBlank } from '@/utils/string';
import { useThrottleFn } from 'ahooks';
import { useMobx } from '@/hooks/useMobx';

// 建议搜索截流时间
const SUGGEST_REQUEST_THROTTLE_WAIT = 600;

// 点击场景
const clickScene = {
  'search-bar': 'tab',
  'search-result': 'results',
} as const;

/** 最大长度 */
const MAX_LENGTH = 800;

const SearchBar: ForwardRefRenderFunction<SearchRef, ISearchBarProps> = (
  {
    onEnter,
    onChange,
    onClose,
    onFocus,
    value,
    scene = 'search-result',
    outerClassName = '',
    innerClassName = '',
    containerClassName = '',
    curtain,
    isMobile = false,
    showCancel = false,
    type,
    defaultValue,
    defaultExpandDropdown,
  },
  ref
) => {
  const { trans } = useTranslation();
  // 搜索文案
  const [searchText, setSearchText] = useState(defaultValue || '');

  // 搜索的推荐内容
  const { fetch, res: recommendResults } = useSearchRecommends();

  const searchInputRef = useRef<SearchInputRef>(null);

  // focus输入框，显示搜索内容抽屉，传入defaultExpendDropdown，支持默认展开
  const [isFocusInput, setFocusStatus] = useState(defaultExpandDropdown || false);

  const { search } = useMobx(['search']);

  // 获取搜索推荐
  const getSuggest = (searchText: string) => {
    // 产品想要字符大于1时才请求，@卢彩凤
    if (searchText.length > 1) {
      fetch(searchText, suggestionBizMap[scene]);
    } else {
      search.updateRecommendsCache();
    }
  };

  // 优先用传入的onEnter
  const _onEnter = (searchStr: string) => {
    if (!searchStr) return;
    // 搜索埋点 - 搜索点击
    SearchTracker.searchBarTracing({
      action: 'search_button_click',
      labels: {
        search: searchStr,
        scene: clickScene[scene],
      },
    });

    // 有onEnter直接用
    if (onEnter) return onEnter(searchStr);

    // 都底为带参跳转结果页
    return redirectToSearchResultsPage({
      search: removeBlank(searchText),
    });
  };

  // 关闭输入框
  const closeSearchInput = useCallback(() => {
    onFocus?.(false);
    onClose?.();
    setFocusStatus(false);
    if (isMobile) {
      // 搜索埋点 - MO cancel 按钮
      SearchTracker.searchBarTracing({
        action: 'search_cancel_click',
      });
    }
  }, [onClose, isMobile, onFocus]);

  // 聚焦输入框
  const onInputFocus = () => {
    onFocus?.(true);
    setFocusStatus(true);
    getSuggest(searchText);
  };

  // 外部控制input相关行为
  useImperativeHandle(ref, () => {
    return {
      focus: () => {
        searchInputRef.current?.focus();
      },
      clear: () => {
        setSearchText('');
      },
      close: () => {
        closeSearchInput();
        searchInputRef.current?.blur();
      },
    };
  });

  // 是否需要蒙层
  const needCurtain = !isMobile && curtain;

  // 下拉框显示内容
  const dropdownContent = (() => {
    // 没聚焦输入框，没有下拉内容
    if (!isFocusInput) return null;

    // 搜索内容字符长度小于1，显示默认推荐模块
    if (searchText.length <= 1) {
      return (
        <SearchRecommends
          scene={`${scene}-default`}
          direction={isMobile ? EnumRowFlexDirection.column : EnumRowFlexDirection.row}
        />
      );
    }

    // 搜索推荐
    return <Suggestions scene={`${scene}-recommend`} data={recommendResults} />;
  })();

  // 搜索埋点 - 搜索框下拉弹层曝光
  useEffect(() => {
    if (isFocusInput) {
      SearchTracker.searchBarTracing({
        action: 'search_bar_expose',
      });
    }
  }, [isFocusInput]);

  // 截流
  const { run: throttleSuggest } = useThrottleFn(getSuggest, {
    wait: SUGGEST_REQUEST_THROTTLE_WAIT,
  });

  // 处理输入框type
  const internalType = (() => {
    if (!isMobile && type === 'border') {
      return isFocusInput ? 'no-border-radius' : 'border';
    }
    return type;
  })();

  useEffect(() => {
    if (value) {
      setSearchText(value);
    }
  }, [value]);

  // 显示默认推荐位时，需要更新缓存
  useEffect(() => {
    if (searchText.length <= 1) {
      search.updateRecommendsCache();
    }
  }, [searchText, search]);

  return (
    <>
      {needCurtain && <Curtain visible={isFocusInput} onClick={closeSearchInput} />}
      <section
        className={cls({
          [styles.inputWrapper]: true,
          [styles.pcCurtain]: needCurtain,
          [outerClassName]: true,
          [styles.focus]: isFocusInput,
        })}
      >
        <div
          className={cls({
            [styles.container]: true,
            [styles.mobile]: isMobile,
            [containerClassName]: true,
          })}
        >
          <div
            className={cls({
              [styles.wrapper]: true,
              [styles.showCancel]: showCancel,
              [innerClassName]: true,
            })}
          >
            <SearchInput
              isMobile={isMobile}
              enterKeyHint={scene === 'search-result' ? 'search' : 'go'}
              maxLength={MAX_LENGTH}
              placeholder={trans('common.search.searchPlaceholders')}
              value={searchText}
              ref={searchInputRef}
              onEnter={() => _onEnter(searchText)}
              onFocus={onInputFocus}
              onChange={(e) => {
                // 最长长度1000，超出截取，但是不限制用户输入
                const inputText = e.target.value.slice(0, MAX_LENGTH);
                setSearchText(inputText);
                if (onChange) {
                  onChange(inputText);
                }
                // 推荐搜索
                throttleSuggest(inputText);
              }}
              type={internalType}
              outerClassName={showCancel ? styles.inputFlexOne : ''}
            />
            {showCancel && (
              <span onClick={closeSearchInput} className={styles.cancelBtn}>
                {trans('common.search.cancel')}
              </span>
            )}
          </div>
          <div className={styles.results}>
            <HeightTransition key={`${scene}-search-results`} show={isFocusInput}>
              <div>{dropdownContent}</div>
            </HeightTransition>
          </div>
        </div>
      </section>
    </>
  );
};

export default memo(forwardRef(SearchBar));
